import { useCallback, useRef, useState, type ReactNode } from "react";
import { DndContext, KeyboardSensor, useSensor, useSensors, type Announcements, type CollisionDetection, type DragStartEvent, type KeyboardCoordinateGetter, type UniqueIdentifier } from "@dnd-kit/core";
import { SortableContext } from "@dnd-kit/sortable";
import { cx } from "../utils/format";
import { insertionLine, keyStep, landingIndex, nestedKeyStep, nestedLanding, type NestedGroupBox, type NestedPlace } from "../utils/sortableLanding";
import { DragLayer, QuickClickPointerSensor, EDGE, Handle, holdEarlyKeys, INSTRUCTIONS, KEYS, PICK_UP_AFTER, stayInPlace, useEscapeClaim, type DropLine } from "./sortableParts";

/** The kit's SortableList in its nested mode (the sidebar editor rework, 2026-10-26, brief docs/briefs/sidebar-rework-round.md item 2): groups
 *  that move among themselves, and entries that move within their group and into another one, in one drag (one dnd-kit context). The look and
 *  the rules are the flat list's (`SortableList`): rows stay in place, the carried row dimmed, a ghost (a label box) follows the pointer, the
 *  accent line marks where the row lands, nothing glides; Space picks up and drops, the arrows move, Escape cancels only the drag; the app's
 *  words for screen readers, naming the group.
 *
 *  The screen draws through the render prop: each group's element spreads `group(id)`, its heading row `head()`, each entry's row
 *  `entry(id)`; the handles are `groupHandle(id, name)` and `entryHandle(id, name)`. Inside the drag the ids carry a prefix per kind
 *  (`group:` / `entry:`), so a group and an entry may share an id. While a group is carried only groups are targets; while an entry is carried,
 *  entries, headings and whole sections are (`nestedLanding`: another group's heading or its foot means its end, an empty group its first
 *  place). The drop answers `onMoveGroup(groupId, toIndex)` or `onMoveEntry(entryId, toGroupId, toIndex)`, the place after the move. */
export interface SortableGroupModel { id: string; /** the group's name, for the spoken words */ label: string; /** its entries' ids, in order */ items: readonly string[] }
export interface SortableGroupRows {
  group: (groupId: string, className?: string) => { "data-drag-group": ""; className: string | undefined };
  groupHandle: (groupId: string, name: string) => ReactNode;
  head: () => { "data-drag-head": "" };
  entry: (entryId: string, className?: string) => { "data-drag-row": ""; className: string | undefined };
  entryHandle: (entryId: string, name: string) => ReactNode;
}
type Props = { groups: readonly SortableGroupModel[]; onMoveGroup: (groupId: string, toIndex: number) => void; onMoveEntry: (entryId: string, toGroupId: string, toIndex: number) => void; children: (rows: SortableGroupRows) => ReactNode };

type Carried = { kind: "group"; id: string; from: number; at: number } | { kind: "entry"; id: string; from: NestedPlace; at: NestedPlace };
type State = { groups: readonly SortableGroupModel[]; groupEls: Map<string, HTMLElement>; rowEls: Map<string, HTMLElement>; names: Map<string, string>; carried: Carried | null; /** the drag that just ended (dnd-kit speaks the end after it) */ last: Carried | null; keyboard: boolean };
type StateRef = { current: State };
const G = (id: string) => `group:${id}`;
const E = (id: string) => `entry:${id}`;
const rectOf = (el: HTMLElement | undefined) => el?.getBoundingClientRect();
const samePlace = (a: NestedPlace, b: NestedPlace) => a.group === b.group && a.index === b.index;

export function SortableGroups({ groups, onMoveGroup, onMoveEntry, children }: Props) {
  const st = useRef<State>({ groups, groupEls: new Map(), rowEls: new Map(), names: new Map(), carried: null, last: null, keyboard: false });
  st.current.groups = groups;
  const [carry, setCarry] = useState<{ id: string; name: string; width: number } | null>(null);
  const [line, setLine] = useState<DropLine>(null);
  const moves = useRef({ onMoveGroup, onMoveEntry }); moves.current = { onMoveGroup, onMoveEntry };
  const sensors = useSensors(
    useSensor(QuickClickPointerSensor, { activationConstraint: { distance: PICK_UP_AFTER } }),
    useSensor(KeyboardSensor, { keyboardCodes: KEYS, coordinateGetter: useKeyboardStep(st), scrollBehavior: "auto", onActivation: holdEarlyKeys }),
  );
  const aim = () => setLine(lineFor(st.current));
  const end = (drop: boolean) => {
    const s = st.current; const c = s.carried;
    s.last = c; s.carried = null; setCarry(null); setLine(null);
    if (!drop || !c) return;
    if (c.kind === "group" && c.at !== c.from) moves.current.onMoveGroup(c.id, c.at);
    if (c.kind === "entry" && !samePlace(c.at, c.from)) moves.current.onMoveEntry(c.id, s.groups[c.at.group]!.id, c.at.index);
  };
  useEscapeClaim(carry !== null);

  const rows: SortableGroupRows = {
    group: (id, className) => ({ "data-drag-group": "", className: cx(className, carry?.id === G(id) && "ob-row-drag--moving") || undefined }),
    groupHandle: (id, name) => { st.current.names.set(G(id), name); return <Handle key={G(id)} id={G(id)} name={name} rows={st.current.groupEls} row="[data-drag-group]" data={{ kind: "group" }} />; },
    head: () => ({ "data-drag-head": "" }),
    entry: (id, className) => ({ "data-drag-row": "", className: cx(className, carry?.id === E(id) && "ob-row-drag--moving") || undefined }),
    entryHandle: (id, name) => { st.current.names.set(E(id), name); return <Handle key={E(id)} id={E(id)} name={name} rows={st.current.rowEls} data={{ kind: "entry" }} />; },
  };
  const allIds = [...groups.map((g) => G(g.id)), ...groups.flatMap((g) => g.items.map(E))];
  return (
    <DndContext sensors={sensors} collisionDetection={useLanding(st)} autoScroll={{ threshold: { x: 0, y: EDGE } }}
      accessibility={{ announcements: wordsOf(st), screenReaderInstructions: { draggable: INSTRUCTIONS } }}
      onDragStart={(e: DragStartEvent) => {
        const s = st.current; const id = String(e.active.id);
        s.carried = carriedFrom(s.groups, id); s.keyboard = e.activatorEvent instanceof KeyboardEvent;
        const el = id.startsWith("group:") ? s.groupEls.get(id) : s.rowEls.get(id);
        setCarry({ id, name: s.names.get(id) ?? "", width: Math.min(rectOf(el)?.width ?? 320, 480) });
      }}
      onDragMove={aim} onDragOver={aim} onDragEnd={() => end(true)} onDragCancel={() => end(false)}>
      <SortableContext items={allIds} strategy={stayInPlace}>{children(rows)}</SortableContext>
      <DragLayer carry={carry} line={line} />
    </DndContext>
  );
}

/** what is carried, from dnd-kit's prefixed id: a group and its place, or an entry and its place in its group */
function carriedFrom(groups: readonly SortableGroupModel[], id: string): Carried | null {
  if (id.startsWith("group:")) { const raw = id.slice(6); const from = groups.findIndex((g) => g.id === raw); return from < 0 ? null : { kind: "group", id: raw, from, at: from }; }
  const raw = id.slice(6);
  const group = groups.findIndex((g) => g.items.includes(raw));
  if (group < 0) return null;
  const from = { group, index: groups[group]!.items.indexOf(raw) };
  return { kind: "entry", id: raw, from, at: from };
}

/** the groups as measured right now: each section, its heading row, its entry rows (null while one is not on the page) */
function measure(s: State): NestedGroupBox[] | null {
  const out: NestedGroupBox[] = [];
  for (const g of s.groups) {
    const el = s.groupEls.get(G(g.id)); const box = rectOf(el);
    if (!el || !box) return null;
    const head = el.querySelector<HTMLElement>("[data-drag-head]")?.getBoundingClientRect() ?? box;
    out.push({ box, head, rows: g.items.map((id) => rectOf(s.rowEls.get(E(id)))).filter((r): r is DOMRect => !!r) });
  }
  return out;
}

/** the id dnd-kit hears as "over" for a place: a different one for every place, so each keyboard step is spoken */
function overId(s: State, c: Carried): string | null {
  if (c.kind === "group") { const g = s.groups[c.at]; return g ? G(g.id) : null; }
  const g = s.groups[c.at.group];
  if (!g) return null;
  const id = g.items[c.at.index];
  return id !== undefined ? E(id) : G(g.id);
}

/** dnd-kit's collision step, used to work out the landing place from the pointer (`landingIndex` over the groups, or `nestedLanding` for an
 *  entry), or the keyboard's place; it keeps that place and names it */
function useLanding(st: StateRef): CollisionDetection {
  return useCallback(({ pointerCoordinates }) => {
    const s = st.current; const c = s.carried;
    if (!c) return [];
    const boxes = pointerCoordinates ? measure(s) : null;
    if (pointerCoordinates && boxes) {
      if (c.kind === "group") c.at = landingIndex(boxes.map((b) => b.box), pointerCoordinates.y, c.from);
      else c.at = nestedLanding(boxes, pointerCoordinates.y, c.from);
    }
    const id = overId(s, c);
    return id ? [{ id }] : [];
  }, [st]);
}

/** where the line goes: for a group or within the entry's own group the flat rule; in another group on top of the row it lands before, under
 *  its last row at its end, under the heading of an empty group */
function lineFor(s: State): DropLine {
  const c = s.carried; const boxes = c && measure(s);
  if (!c || !boxes) return null;
  if (c.kind === "group") return insertionLine(s.groups.map((g) => rectOf(s.groupEls.get(G(g.id)))!), c.from, c.at);
  const rows = s.groups[c.at.group]!.items.map((id) => rectOf(s.rowEls.get(E(id)))).filter((r): r is DOMRect => !!r);
  if (c.at.group === c.from.group) return insertionLine(rows, c.from.index, c.at.index);
  const before = rows[c.at.index]; const last = rows[rows.length - 1];
  if (before) return { top: before.top - 1, left: before.left, width: before.width };
  if (last) return { top: last.bottom - 1, left: last.left, width: last.width };
  const head = boxes[c.at.group]!.head as DOMRect;
  return { top: head.bottom - 1, left: head.left, width: head.width };
}

/** the top of the place a keyboard step points at, to move the ghost there */
function topOf(s: State, c: Carried): number {
  if (c.kind === "group") return rectOf(s.groupEls.get(G(s.groups[c.at]!.id)))?.top ?? 0;
  const g = s.groups[c.at.group]!;
  const at = rectOf(s.rowEls.get(E(g.items[c.at.index] ?? "")));
  if (at) return at.top;
  const last = rectOf(s.rowEls.get(E(g.items[g.items.length - 1] ?? "")));
  return last?.bottom ?? rectOf(s.groupEls.get(G(g.id)))?.top ?? 0;
}

/** an arrow key while a group or an entry is picked up by the keyboard: one place up or down (`keyStep` among groups, `nestedKeyStep` across
 *  groups' ends for an entry), the ghost moved onto that place */
function useKeyboardStep(st: StateRef): KeyboardCoordinateGetter {
  return useCallback((event, { currentCoordinates }) => {
    const s = st.current; const c = s.carried;
    if (!c) return undefined;
    const was = topOf(s, c);
    if (c.kind === "group") {
      const next = keyStep(c.at, event.code, s.groups.length);
      if (next === null) return undefined;
      event.preventDefault();
      if (next === c.at) return undefined;
      c.at = next;
    } else {
      const next = nestedKeyStep(c.at, event.code, s.groups.map((g) => g.items.length), c.from);
      if (next === null) return undefined;
      event.preventDefault();
      if (samePlace(next, c.at)) return undefined;
      c.at = next;
    }
    return { x: currentCoordinates.x, y: currentCoordinates.y + (topOf(s, c) - was) };
  }, [st]);
}

/** what screen readers hear during a keyboard drag, in the app's words: the row's name, its place and, for an entry, its group ("Spam: place 1
 *  of 3 in AI."); a pointer drag is silent, as in the flat list */
function wordsOf(st: StateRef): Announcements {
  const name = (id: UniqueIdentifier) => st.current.names.get(String(id)) ?? "";
  const say = (words: string) => (st.current.keyboard ? words : undefined);
  const where = (c: Carried, p: "from" | "at") => {
    const s = st.current;
    if (c.kind === "group") return { place: c[p] + 1, of: s.groups.length, group: "" };
    const at = c[p]; const g = s.groups[at.group]!;
    return { place: at.index + 1, of: g.items.length + (at.group === c.from.group ? 0 : 1), group: ` in ${g.label}` };
  };
  const moved = (c: Carried) => (c.kind === "group" ? c.at !== c.from : !samePlace(c.at, c.from));
  const now = () => st.current.carried ?? st.current.last;
  return {
    onDragStart: ({ active }) => { const c = now(); if (!c) return undefined; const w = where(c, "from"); return say(`${name(active.id)}: place ${w.place} of ${w.of}${w.group}. The arrow keys move it, Space drops it, Escape puts it back.`); },
    onDragOver: ({ active }) => { const c = now(); if (!c) return undefined; const w = where(c, "at"); return say(`${name(active.id)}: place ${w.place} of ${w.of}${w.group}.`); },
    onDragEnd: ({ active }) => { const c = now(); if (!c) return undefined; const w = where(c, "at"); return say(moved(c) ? `${name(active.id)} moved to place ${w.place}${w.group}.` : `${name(active.id)} stays where it was.`); },
    onDragCancel: ({ active }) => say(`${name(active.id)} stays where it was.`),
  };
}
